import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Input } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { Card } from '../../components/UI/Card';
import { User, Mail, Lock, UserPlus, AlertCircle } from 'lucide-react';
import { Modal } from '../../components/UI/Modal';
import { Privacy } from '../Privacy';
import { Terms } from '../Terms';

export const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms: true,
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [modalContent, setModalContent] = useState(null); // 'terms' or 'privacy' or null
  const { register } = useAuth();
  const { success } = useToast();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setErrors({});

    if (formData.password !== formData.password_confirmation) {
      setErrorMessage('Las contraseñas no coinciden.');
      return;
    }

    if (!formData.terms) {
      setErrorMessage('Debes aceptar los términos y condiciones para continuar.');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        terms: formData.terms ? 1 : 0,
      });
      success('¡Cuenta creada exitosamente! Bienvenido a Quinvoice.');
      navigate('/');
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      setErrorMessage(
        err.data?.message || err.message || 'Ocurrió un error al registrar la cuenta.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-2xl bg-[#08707A] text-white flex items-center justify-center font-black text-2xl mx-auto shadow-xl shadow-teal-900/20">
            Q
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            Crear Cuenta
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Comienza a gestionar tus cotizaciones, facturas e inventario
          </p>
        </div>

        <Card className="p-8 shadow-xl border-slate-200/80 dark:border-slate-800">
          {errorMessage && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-xs flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 font-medium leading-relaxed">{errorMessage}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Nombre Completo o Empresa"
              type="text"
              name="name"
              placeholder="Juan Pérez"
              icon={User}
              value={formData.name}
              onChange={handleChange}
              error={errors.name?.[0]}
              required
              autoFocus
            />

            <Input
              label="Correo Electrónico"
              type="email"
              name="email"
              placeholder="tu@correo.com"
              icon={Mail}
              value={formData.email}
              onChange={handleChange}
              error={errors.email?.[0]}
              required
            />

            <Input
              label="Contraseña (mínimo 6 caracteres)"
              type="password"
              name="password"
              placeholder="••••••••"
              icon={Lock}
              value={formData.password}
              onChange={handleChange}
              error={errors.password?.[0]}
              required
            />

            <Input
              label="Confirmar Contraseña"
              type="password"
              name="password_confirmation"
              placeholder="••••••••"
              icon={Lock}
              value={formData.password_confirmation}
              onChange={handleChange}
              required
            />

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="terms"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <label htmlFor="terms" className="text-xs text-slate-600 dark:text-slate-400">
                Acepto los <button type="button" onClick={() => setModalContent('terms')} className="font-bold text-[#08707A] hover:underline dark:text-teal-400">Términos de Servicio</button> y la <button type="button" onClick={() => setModalContent('privacy')} className="font-bold text-[#08707A] hover:underline dark:text-teal-400">Política de Privacidad</button>
              </label>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={loading}
                icon={UserPlus}
                className="w-full bg-[#08707A] hover:bg-[#065B63]"
              >
                Registrarme Gratis
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 text-center">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              ¿Ya tienes una cuenta?{' '}
              <Link
                to="/login"
                className="font-bold text-[#08707A] hover:text-[#065B63] dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
              >
                Inicia sesión aquí
              </Link>
            </p>
          </div>
        </Card>
      </div>

      <Modal
        isOpen={modalContent !== null}
        onClose={() => setModalContent(null)}
        maxWidth="max-w-4xl"
      >
        <div className="p-4 sm:p-6 bg-white dark:bg-surface-800 rounded-3xl">
          {modalContent === 'terms' && <Terms hideLayout />}
          {modalContent === 'privacy' && <Privacy hideLayout />}
        </div>
      </Modal>
    </div>
  );
};
